import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import ReactQuill from "react-quill-new";

import "react-quill-new/dist/quill.snow.css";

const quillModules = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ header: [1, 2, 3, false] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["blockquote"],
    ["clean"],
  ],
};

const quillFormats = [
  "bold",
  "italic",
  "underline",
  "strike",
  "header",
  "list",
  "align",
  "blockquote",
];

const isRichTextEmpty = (html = "") => {
  const text = String(html)
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .trim();

  return !text;
};

const StepRequestDetail = ({ data, onNext, onBack, submitting, onError }) => {
  const [form, setForm] = useState({
    description: data?.description || "",
    notes: data?.notes || "",
    document: data?.document || null,
    startDate: data?.startDate || null,
    endDate: data?.endDate || null,
  });

  const [errors, setErrors] = useState({});

  /* =========================
     PDF PREVIEW URL
  ========================= */

  const documentPreviewUrl = useMemo(() => {
    if (
      !form.document ||
      !(form.document instanceof File) ||
      form.document.type !== "application/pdf"
    ) {
      return null;
    }

    return URL.createObjectURL(form.document);
  }, [form.document]);

  useEffect(() => {
    return () => {
      if (documentPreviewUrl) {
        URL.revokeObjectURL(documentPreviewUrl);
      }
    };
  }, [documentPreviewUrl]);

  /* =========================
     HELPERS
  ========================= */

  const requiredLabel = (label) => (
    <label className="text-sm font-semibold mb-1.5 text-text-secondary sm:text-base">
      {label} <span className="text-red-500">*</span>
    </label>
  );

  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const allowedExtensions = [".pdf", ".doc", ".docx"];

  const maxSize = 10 * 1024 * 1024;

  /* =========================
     FILE
  ========================= */

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const fileName = file.name.toLowerCase();

    const isValidType =
      allowedTypes.includes(file.type) ||
      allowedExtensions.some((ext) => fileName.endsWith(ext));

    if (!isValidType) {
      e.target.value = "";

      setForm((prev) => ({
        ...prev,
        document: null,
      }));

      onError?.(
        "Format dokumen tidak didukung. Silakan upload file dengan format PDF, DOC, atau DOCX.",
      );

      return;
    }

    if (file.size > maxSize) {
      e.target.value = "";

      setForm((prev) => ({
        ...prev,
        document: null,
      }));

      onError?.("Ukuran file maksimal 10 MB.");

      return;
    }

    setErrors((prev) => ({
      ...prev,
      document: "",
    }));

    setForm((prev) => ({
      ...prev,
      document: file,
    }));
  };

  /* =========================
     VALIDATION
  ========================= */

  const validate = () => {
    const newErrors = {};

    if (isRichTextEmpty(form.description)) {
      newErrors.description = "Description Project wajib diisi.";
    }

    if (!form.document) {
      newErrors.document = "Support Document wajib diupload.";
    }

    if (!form.startDate) {
      newErrors.startDate = "Event Date wajib diisi.";
    }

    if (!form.endDate) {
      newErrors.endDate = "End Date wajib diisi.";
    }

    if (
      form.startDate &&
      form.endDate &&
      new Date(form.endDate) < new Date(form.startDate)
    ) {
      newErrors.endDate = "End Date tidak boleh lebih awal dari Event Date.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /* =========================
     SUBMIT
  ========================= */

  const handleSubmit = () => {
    if (!validate()) return;

    onNext(form);
  };

  return (
    <>
      {/* =========================
          QUILL RESPONSIVE STYLE
      ========================= */}

      <style>{`
        .description-editor .ql-toolbar {
          border: none !important;
          border-bottom: 1px solid #e5e7eb !important;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 2px;
          padding: 8px;
        }

        .description-editor .ql-container {
          border: none !important;
          min-height: 180px;
          max-height: 260px;
          overflow-y: auto;
          font-family: inherit;
        }

        .description-editor .ql-editor {
          min-height: 180px;
          padding: 14px;
          font-size: 14px;
          line-height: 1.6;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .description-editor .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }

        .description-editor .ql-picker-options {
          z-index: 9999;
        }

        @media (min-width: 640px) {
          .description-editor .ql-toolbar {
            padding: 10px;
          }

          .description-editor .ql-container {
            min-height: 210px;
            max-height: 300px;
          }

          .description-editor .ql-editor {
            min-height: 210px;
            padding: 16px;
          }
        }

        @media (min-width: 768px) {
          .description-editor .ql-container {
            min-height: 220px;
            max-height: 320px;
          }

          .description-editor .ql-editor {
            min-height: 220px;
          }
        }

        @media (min-width: 1024px) {
          .description-editor .ql-container {
            min-height: 240px;
            max-height: 360px;
          }

          .description-editor .ql-editor {
            min-height: 240px;
          }
        }
      `}</style>

      <div className="w-full">
        {/* =========================
            FORM GRID
        ========================= */}

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:gap-8">
          {/* =========================
              DESCRIPTION PROJECT
          ========================= */}

          <div className="flex flex-col md:col-span-2">
            {requiredLabel("Description Project")}

            <div
              className={`description-editor overflow-hidden bg-white border rounded-xl transition ${
                errors.description
                  ? "border-red-400"
                  : "border-text-light focus-within:border-brand-first"
              }`}
            >
              <ReactQuill
                theme="snow"
                value={form.description}
                onChange={(value) => {
                  setForm((prev) => ({
                    ...prev,
                    description: value,
                  }));

                  if (errors.description) {
                    setErrors((prev) => ({
                      ...prev,
                      description: "",
                    }));
                  }
                }}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Input Description"
              />
            </div>

            {errors.description && (
              <p className="text-xs text-red-500 mt-1.5">
                {errors.description}
              </p>
            )}
          </div>

          {/* =========================
              ADDITIONAL NOTES
          ========================= */}

          <div className="flex flex-col">
            <label className="text-sm font-semibold mb-1.5 text-text-secondary sm:text-base">
              Additional Notes
            </label>

            <textarea
              placeholder="Add Notes"
              className="w-full p-4 text-sm transition border outline-none resize-y min-h-[140px] border-text-light rounded-xl sm:min-h-[160px] focus:border-brand-first focus:ring-2 focus:ring-brand-first/20"
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
            />
          </div>

          {/* =========================
              SUPPORT DOCUMENT
          ========================= */}

          <div className="flex flex-col">
            {requiredLabel("Support Document")}

            <div
              className={`
                overflow-hidden
                border
                rounded-xl
                min-h-[140px]
                sm:min-h-[160px]
                flex
                flex-col
                items-center
                justify-center
                text-center
                transition
                ${errors.document ? "border-red-400" : "border-text-light"}
              `}
            >
              <input
                type="file"
                className="hidden"
                id="upload-doc"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileChange}
              />

              {!form.document ? (
                <label
                  htmlFor="upload-doc"
                  className="flex flex-col items-center justify-center w-full px-4 py-5 transition cursor-pointer min-h-[140px] sm:min-h-[160px] hover:bg-surface-background"
                >
                  <p className="mb-3 text-sm text-text-secondary">
                    Upload Document
                  </p>

                  <div className="flex flex-wrap justify-center gap-2">
                    {["PDF", "DOC", "DOCX", "Max 10 MB"].map((item) => (
                      <span
                        key={item}
                        className="px-3 py-1 text-xs rounded-full bg-surface-background text-text-secondary whitespace-nowrap"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </label>
              ) : form.document?.type === "application/pdf" ? (
                <div className="w-full">
                  <div className="flex items-center justify-between gap-3 px-3 py-2 border-b sm:px-4 bg-gray-50">
                    <p className="min-w-0 text-xs font-medium truncate text-text-secondary">
                      {form.document.name}
                    </p>

                    <label
                      htmlFor="upload-doc"
                      className="flex-shrink-0 text-xs font-semibold cursor-pointer text-brand-first hover:underline"
                    >
                      Change
                    </label>
                  </div>

                  {documentPreviewUrl && (
                    <iframe
                      src={documentPreviewUrl}
                      title="PDF Preview"
                      className="w-full h-[220px] sm:h-[260px] md:h-[280px]"
                    />
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center w-full px-4 py-5 min-h-[140px] sm:min-h-[160px]">
                  <p className="max-w-full text-sm font-semibold break-all text-text-primary">
                    {form.document?.name}
                  </p>

                  <p className="mt-1 text-xs text-text-secondary">
                    Preview hanya tersedia untuk file PDF.
                  </p>

                  <label
                    htmlFor="upload-doc"
                    className="mt-3 text-xs font-semibold cursor-pointer text-brand-first hover:underline"
                  >
                    Change Document
                  </label>
                </div>
              )}
            </div>

            {errors.document && (
              <p className="text-xs text-red-500 mt-1.5">{errors.document}</p>
            )}
          </div>

          {/* =========================
              EVENT DATE
          ========================= */}

          <div className="flex flex-col min-w-0">
            {requiredLabel("Event Date")}

            <DatePicker
              selected={form.startDate}
              onChange={(date) => {
                setForm((prev) => ({
                  ...prev,
                  startDate: date,
                  endDate:
                    prev.endDate && date && prev.endDate < date
                      ? null
                      : prev.endDate,
                }));

                setErrors((prev) => ({
                  ...prev,
                  startDate: "",
                  endDate: "",
                }));
              }}
              dateFormat="dd/MM/yyyy"
              placeholderText="dd/mm/yyyy"
              wrapperClassName="w-full"
              className={`
                w-full
                p-3
                sm:p-3.5
                border
                rounded-xl
                outline-none
                transition
                focus:ring-2
                focus:ring-brand-first/20
                focus:border-brand-first
                ${errors.startDate ? "border-red-400" : "border-text-light"}
              `}
            />

            {errors.startDate && (
              <p className="text-xs text-red-500 mt-1.5">{errors.startDate}</p>
            )}
          </div>

          {/* =========================
              END DATE
          ========================= */}

          <div className="flex flex-col min-w-0">
            {requiredLabel("End Date")}

            <DatePicker
              selected={form.endDate}
              onChange={(date) => {
                setForm((prev) => ({
                  ...prev,
                  endDate: date,
                }));

                if (errors.endDate) {
                  setErrors((prev) => ({
                    ...prev,
                    endDate: "",
                  }));
                }
              }}
              minDate={form.startDate || undefined}
              dateFormat="dd/MM/yyyy"
              placeholderText="dd/mm/yyyy"
              wrapperClassName="w-full"
              className={`
                w-full
                p-3
                sm:p-3.5
                border
                rounded-xl
                outline-none
                transition
                focus:ring-2
                focus:ring-brand-first/20
                focus:border-brand-first
                ${errors.endDate ? "border-red-400" : "border-text-light"}
              `}
            />

            {errors.endDate && (
              <p className="text-xs text-red-500 mt-1.5">{errors.endDate}</p>
            )}
          </div>
        </div>

        {/* =========================
            ACTION BUTTONS
        ========================= */}

        <div className="flex flex-col-reverse justify-between gap-3 mt-6 sm:flex-row sm:items-center sm:gap-4">
          <button
            type="button"
            onClick={onBack}
            disabled={submitting}
            className="w-full px-6 transition border rounded-lg sm:w-auto py-2.5 border-text-secondary hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Back
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full py-3 font-medium transition shadow-lg sm:w-auto px-7 bg-brand-first text-surface-background rounded-xl hover:bg-brand-primary disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </>
  );
};

export default StepRequestDetail;
